import React, { useCallback, memo, useMemo } from 'react';
import styled from 'styled-components';
import { currency, percentage, forceTwoDecimalPoints } from '@sunbit-dev/common/helpers/numbers';
import * as styles from './PaymentPlan.styles';
import { PaymentPlanProps } from './models';
import classNames from 'classnames';
import { PaymentPlanSkeleton } from './components/paymentPlanSkeleton/PaymentPlanSkeleton';
import SStrong from '../SStrong/SStrong';

// To support zero currency
const formatter = { format: { zero: '%s%v' } };

export const PaymentPlan = memo(styled(
  ({
    className,
    numOfPayments,
    monthlyPaymentRate,
    effectiveApr: _effectiveApr,
    financialCharge,
    amountFinanced,
    totalDownPayment,
    isSelected,
    id,
    onClick: _onClick,
    isSkeleton,
    isLoading,
    monthlyAmountSymbol = '^',
  }: PaymentPlanProps) => {
    const onClick = useCallback(() => {
      !isLoading && _onClick(id);
    }, [_onClick, id, isLoading]);

    const effectiveApr = useMemo(() => {
      const parsedNumber = +forceTwoDecimalPoints(_effectiveApr);

      return parsedNumber > 0 ? _effectiveApr : 0;
    }, [_effectiveApr]);

    return (
      <div
        {...{
          'data-testid': `payment-plan-${numOfPayments}`,
          className: classNames(className, isSelected && !isSkeleton ? 'expanded' : 'collapsed'),
          onClick,
        }}
      >
        {isLoading || isSkeleton ? (
          <PaymentPlanSkeleton {...{ isLoading }} />
        ) : (
          <div className="container">
            <section className="top-section">
              <h1 data-testid="payments-amount" className="payments-amount">
                {numOfPayments}
              </h1>
              <p className="monthly-payments">monthly payments of</p>
              <h3 data-testid="monthly-payment-rate" className="monthly-payment-rate">
                {currency(monthlyPaymentRate)}
                <span className="sign">{monthlyAmountSymbol}</span>
              </h3>
            </section>
            <section className="bottom-section">
              <div className="column">
                <span className="title">APR</span>
                <div className="amount-txt">
                  <SStrong data-testid="apr-amount">{percentage(effectiveApr)}</SStrong>
                </div>
              </div>

              <div className="column">
                <span className="title">Interest</span>
                <div className="amount-txt">
                  <p className="finance">
                    <SStrong className="number" data-testid="finance-charge-amount">
                      {currency(financialCharge, formatter)}
                    </SStrong>
                    <br />
                    {`(${currency(
                      financialCharge ? financialCharge / numOfPayments : financialCharge,
                      formatter
                    )} avg/mo)`}
                  </p>
                </div>
              </div>

              <div className="column">
                <span className="title">Total Cost</span>
                <div className="amount-txt">
                  <SStrong className="total-amount-text" data-testid="total-amount">
                    {currency(financialCharge + amountFinanced + totalDownPayment)}
                  </SStrong>
                </div>
              </div>
            </section>
          </div>
        )}
      </div>
    );
  }
)`
  ${styles.PaymentPlan}
`);
